/**
 * «Что с ним делать» (§2.2.3): три статуса одним нажатием плюс «Сломан» с
 * причиной.
 *
 * Все четыре — прямая смена статуса байка (`MarkInTransit`/`MarkAtBase`/
 * `MarkRecovered`/`MarkFaulty`), доступная СРАЗУ, без задачи: главный
 * полевой случай — «нашёл байк, задачи на него нет» — не должен требовать
 * сначала завести задачу, чтобы просто сказать «он теперь в машине».
 * Задача (раздел «Задание» ниже, если она есть) — отдельный, необязательный
 * слой поверх той же машины состояний.
 */
import { useState } from 'react'
import {
  statusActionLabel,
  statusBlockedReason,
  useSetVehicleStatus,
} from '@/api/vehicles'
import { BigButton, BottomSheet, Notice } from '@/components/ui'

const FAULT_REASONS = [
  'нет седла',
  'тормоза',
  'колесо',
  'замок',
  'электрика',
  'другое',
] as const
type FaultReason = (typeof FAULT_REASONS)[number]

function FaultSheet({
  open,
  busy,
  onClose,
  onSubmit,
}: {
  open: boolean
  busy: boolean
  onClose: () => void
  onSubmit: (reason: string) => void
}) {
  const [picked, setPicked] = useState<FaultReason | null>(null)
  const [text, setText] = useState('')

  const composed = picked === 'другое' ? text.trim() : (picked ?? '')
  const canSubmit = picked !== null && composed !== ''

  return (
    <BottomSheet
      open={open}
      onClose={() => {
        setPicked(null)
        setText('')
        onClose()
      }}
    >
      <h2 className="mb-3 text-lg font-medium">Что сломано</h2>
      <div className="flex flex-wrap gap-2">
        {FAULT_REASONS.map((r) => (
          <button
            key={r}
            aria-pressed={picked === r}
            onClick={() => {
              setPicked(r)
            }}
            className={`min-h-11 rounded-full px-4 text-sm font-medium transition ${
              picked === r
                ? 'bg-red-500 text-black'
                : 'border border-white/20 text-white/80 active:bg-white/10'
            }`}
          >
            {r === 'другое' ? 'Другое' : r[0]!.toUpperCase() + r.slice(1)}
          </button>
        ))}
      </div>

      {picked === 'другое' ? (
        <input
          className="mt-3 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2"
          value={text}
          onChange={(e) => {
            setText(e.target.value)
          }}
          placeholder="Что именно"
          autoFocus
        />
      ) : null}

      <div className="mt-4 flex gap-2">
        <BigButton
          tone="danger"
          busy={busy}
          disabled={!canSubmit}
          onClick={() => {
            onSubmit(composed)
          }}
        >
          Сломан
        </BigButton>
        <BigButton
          tone="neutral"
          onClick={() => {
            setPicked(null)
            setText('')
            onClose()
          }}
        >
          Отмена
        </BigButton>
      </div>
    </BottomSheet>
  )
}

export function BikeActions({ vehicleId, status }: { vehicleId: string; status: string }) {
  const setStatus = useSetVehicleStatus()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  function set(action: 'in_transit' | 'at_base' | 'available', reason: string) {
    setDone(null)
    setStatus.mutate(
      { vehicleId, action, reason },
      { onSuccess: () => { setDone(statusActionLabel[action]) } },
    )
  }

  const inTransitBlocked = statusBlockedReason(status, 'in_transit')
  const atBaseBlocked = statusBlockedReason(status, 'at_base')
  const availableBlocked = statusBlockedReason(status, 'available')
  const faultyBlocked = statusBlockedReason(status, 'faulty')

  return (
    <section className="mb-4">
      <h2 className="mb-2 text-sm font-medium uppercase tracking-wide opacity-50">
        Что с ним делать
      </h2>

      {done ? (
        <div className="mb-2">
          <Notice tone="ok">
            «{done}» — принято. Уйдёт сразу, а без сети — как появится связь.
          </Notice>
        </div>
      ) : null}

      {setStatus.isError ? (
        <div className="mb-2">
          <Notice tone="error">
            Действие не сохранено на устройстве. Проверьте, что браузеру разрешено
            хранить данные, и повторите.
          </Notice>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <BigButton
          tone="neutral"
          disabled={!!inTransitBlocked}
          busy={setStatus.isPending && setStatus.variables?.action === 'in_transit'}
          onClick={() => { set('in_transit', '') }}
        >
          В машину
        </BigButton>
        <BigButton
          tone="neutral"
          disabled={!!atBaseBlocked}
          busy={setStatus.isPending && setStatus.variables?.action === 'at_base'}
          onClick={() => { set('at_base', '') }}
        >
          На базу
        </BigButton>
        <BigButton
          tone="neutral"
          disabled={!!availableBlocked}
          busy={setStatus.isPending && setStatus.variables?.action === 'available'}
          onClick={() => { set('available', 'Поставлено скаутом') }}
        >
          Поставил здесь
        </BigButton>
        <BigButton
          tone="danger"
          disabled={!!faultyBlocked}
          onClick={() => { setSheetOpen(true) }}
        >
          Сломан
        </BigButton>
      </div>

      <FaultSheet
        open={sheetOpen}
        busy={setStatus.isPending && setStatus.variables?.action === 'faulty'}
        onClose={() => { setSheetOpen(false) }}
        onSubmit={(reason) => {
          setStatus.mutate(
            { vehicleId, action: 'faulty', reason },
            {
              onSuccess: () => {
                setDone(statusActionLabel.faulty)
                setSheetOpen(false)
              },
            },
          )
        }}
      />
    </section>
  )
}
